import React, { useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Divider, Grid, IconButton, List, ListItem, Typography } from '@mui/material';
import { Edit as EditIcon, LockReset as LockResetIcon } from '@mui/icons-material';
import {
  ExternalLinkButton,
  GenderAgeDisplay,
  InfoFieldItem,
  MaskedEmailDisplay,
  MemoEditDialog,
  PasswordResetDialog,
  SectionWithPaper,
  TimeDisplay
} from '../common';
import { updateNurseMemo } from '../../api/nurses';
import {
  CANCER_NURSING_EXPERIENCE_TYPES,
  CANCER_PATIENT_PARTY_TYPES,
  Nurse,
  NURSE_CHILDREN_TYPES,
  NURSE_PET_TYPES,
  NURSE_TYPE_THEME,
  NURSING_WORK_EXPERIENCE_TYPES,
  REPLY_PERIOD_TYPES,
  ROLE_COLORS,
  ROLE_TYPES
} from '../../types/nurse';
import { getEmploymentLabel, getPrefectureLabel } from '../../types/common';
import { getExternalNurseUrl } from '../../utils/urlUtils';
import { calculateNursingExperienceYears } from "../../utils/dateUtils";

interface ProfileSectionProps {
  nurse: Nurse;
  onNurseUpdate?: () => void; // 看護師情報更新時のコールバック
}

/**
 * 汎用リスト型データの整形関数
 * @param listValue リスト値（文字列または文字列配列）
 * @param typeMap 変換に使用する定数マップ（例: NURSE_PET_TYPES）
 * @param otherValue "OTHER"に対応する値（任意）
 * @param separator 結合時の区切り文字（デフォルト: ", "）
 * @returns フォーマットされた文字列
 */
const formatListValue = (
  listValue: string | null,
  typeMap: Record<string, string>,
  otherValue: string | null = null,
  separator = ', '
): string => {
  if (!listValue || listValue === '[]' || listValue === '') {
    return '[未入力]';
  }
  try {
    let items: string[] = [];
    // 文字列からリストへの変換処理
    if (typeof listValue === 'string') {
      const fixedJsonStr = listValue.replace(/'/g, '"');
      try {
        // JSONとしてパースを試みる
        if (fixedJsonStr.startsWith('[') && fixedJsonStr.endsWith(']')) {
          items = JSON.parse(fixedJsonStr);
        }
      } catch (parseError) {
        // JSONパースに失敗した場合、カンマ区切り文字列として処理
        const strippedList = listValue.replace(/[[\]']/g, '');
        items = strippedList.split(',').map(item => item.trim());
      }
    }
    // 空のリストの場合
    if (items.length === 0) {
      return '[未入力]';
    }
    // リスト値の変換
    const translatedItems = items.map(item => {
      if (!item || item === '') return null;
      // 引用符を除去して整形
      const cleanItem = item.replace(/['"]/g, '').trim();
      // "OTHER"タイプの特別処理
      if (cleanItem === 'OTHER' && otherValue) {
        return otherValue;
      }
      // typeMapを使用して変換
      return typeMap[cleanItem as keyof typeof typeMap] || cleanItem;
    }).filter(Boolean); // null/undefinedを除外
    // 変換後も空の場合
    if (translatedItems.length === 0) {
      return '[未入力]';
    }
    // 結果を区切り文字で結合
    return translatedItems.join(separator);
  } catch (e) {
    // エラー時は元の値をそのまま返す
    return listValue || '[未入力]';
  }
};
// パスワードリセットの有効期限を計算する関数
const calculatePasswordResetExpiry = (resetSentAt: string): { isExpired: boolean; remainingTime: string } => {
  const sentTime = new Date(resetSentAt).getTime();
  const expiryTime = sentTime + (6 * 60 * 60 * 1000); // 6時間後
  const now = new Date().getTime();
  if (now > expiryTime) {
    return { isExpired: true, remainingTime: '有効期限切れ' };
  }
  const remaining = expiryTime - now;
  const hours = Math.floor(remaining / (60 * 60 * 1000));
  const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
  if (hours > 0) {
    return { isExpired: false, remainingTime: `残り ${hours}時間${minutes}分` };
  } else {
    return { isExpired: false, remainingTime: `残り ${minutes}分` };
  }
};
// 未入力表示用のスタイル
const getUnsetStyle = (value: string) => {
  return value === '[未入力]' ? {
    color: 'text.disabled',
    fontWeight: 'normal',
    fontStyle: 'italic',
  } : {
    fontWeight: 'bold',
  };
};
const getRoleLabel = (roleType: string): string => {
  return ROLE_TYPES[roleType as keyof typeof ROLE_TYPES] || roleType;
};
const getReplyPeriodLabel = (period: string): string => {
  return REPLY_PERIOD_TYPES[period as keyof typeof REPLY_PERIOD_TYPES] || period;
};
const getCancerNursingExperienceLabel = (experience: string | null): string => {
  if (!experience) return '[未入力]';
  return CANCER_NURSING_EXPERIENCE_TYPES[experience as keyof typeof CANCER_NURSING_EXPERIENCE_TYPES] || experience;
};
// がん患者との関係タイプリストのフォーマット（区切り文字に"、"を使用）
const formatCancerPatientPartyTypes = (typeList: string | null, otherType: string | null): string => {
  return formatListValue(typeList, CANCER_PATIENT_PARTY_TYPES, otherType, '、');
};
// 看護経験リストのフォーマット（区切り文字に"、"を使用）
const formatNursingExperience = (experienceList: string | null, otherExperience: string | null): string => {
  return formatListValue(experienceList, NURSING_WORK_EXPERIENCE_TYPES, otherExperience, '、');
};
const ProfileSection: React.FC<ProfileSectionProps> = ({ nurse, onNurseUpdate }) => {
  const [ isEditDialogOpen, setIsEditDialogOpen ] = useState(false);
  const [ isPasswordResetDialogOpen, setIsPasswordResetDialogOpen ] = useState(false);
  // パスワードリセットの有効期限を計算
  const passwordResetExpiry = useMemo(() => {
    if (!nurse.reset_password_sent_at) return null;
    return calculatePasswordResetExpiry(nurse.reset_password_sent_at);
  }, [ nurse.reset_password_sent_at ]);
  const handleEditClick = () => {
    setIsEditDialogOpen(true);
  };
  const handlePasswordResetClick = () => {
    setIsPasswordResetDialogOpen(true);
  };
  const handleMemoSubmit = async(memo: string) => {
    await updateNurseMemo(nurse.id, memo);
    // 看護師詳細画面をリロード
    if (onNurseUpdate) {
      onNurseUpdate();
    } else {
      // フォールバック: ページ全体をリロード
      window.location.reload();
    }
  };
  return (
    <Card
      sx={{
        mb: 3,
        border: `1px solid ${nurse.role_type === 'STAFF' ? NURSE_TYPE_THEME.TRAINING.BORDER : NURSE_TYPE_THEME.NORMAL.BORDER}`,
        borderLeft: `4px solid ${nurse.role_type === 'STAFF' ? NURSE_TYPE_THEME.TRAINING.BORDER : NURSE_TYPE_THEME.NORMAL.BORDER}`,
        boxShadow: `0 2px 8px ${nurse.role_type === 'STAFF' ? NURSE_TYPE_THEME.TRAINING.BACKGROUND : NURSE_TYPE_THEME.NORMAL.BACKGROUND}50`
      }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        {/* 申し込み可否状態 - 研修看護師(STAFF)以外かつ申込み不可の場合のみ表示 */}
        {nurse.role_type !== 'STAFF' && !nurse.can_assign && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 60,
              height: 60,
              mr: 2,
              backgroundColor: '#ffebee',
              border: '2px solid',
              borderColor: '#d32f2f',
              borderRadius: '50%',
            }}
          >
            {/* マーク（✕）を大きく表示 */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                component="div"
                sx={{
                  color: '#b71c1c',
                  fontWeight: 900,
                  fontSize: '1.4rem',
                  lineHeight: 1,
                  mb: 0.3
                }}
              >
                ✕
              </Typography>

              {/* テキスト */}
              <Typography
                variant="caption"
                sx={{
                  color: '#c62828',
                  fontWeight: 'bold',
                  fontSize: '0.65rem',
                  display: 'block'
                }}
              >
                申込み不可
              </Typography>
            </Box>
          </Box>
        )}

        {/* 名前とニックネーム（上下に配置） */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h5" component="h2" sx={{ lineHeight: 1.3, fontSize: '1.8rem', fontWeight: 'bold' }}>
                {nurse.name}
              </Typography>
              <GenderAgeDisplay
                genderType={nurse.gender_type}
                birthday={nurse.birthday}
                showTooltip
                tooltipPlacement="top"
                fontSize="1.2rem"
                color="text.secondary"
                fontWeight="normal"
                marginLeft={1.5}
              />
            </Box>

            <ExternalLinkButton
              href={getExternalNurseUrl(nurse.id)}
              tooltip="看護師ページをアプリで開く"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.95rem', mr: 0.5 }}>
              ニックネーム:
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
              {nurse.nickname}
            </Typography>
          </Box>
        </Box>

        {/* スペーサー */}
        <Box sx={{ flexGrow: 1 }}/>

        {/* 右上の情報 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          {/* 登録日時 - 人間が読みやすい形式 */}
          <TimeDisplay
            timestamp={nurse.created_at}
            prefix="登録"
            tooltipPrefix="登録日時"
            tooltipPlacement="left"
            fontSize="0.9rem"
          />

          {/* 退会日時 - 退会済みの場合のみ表示 */}
          {nurse.resigned_at && (
            <TimeDisplay
              timestamp={nurse.resigned_at}
              prefix="退会"
              tooltipPrefix="退会日時"
              tooltipPlacement="left"
              color="error.main"
              iconColor="error"
              fontSize="0.9rem"
              fontWeight="medium"
            />
          )}

          {/* ロール (右端・大きめ) */}
          <Chip
            label={getRoleLabel(nurse.role_type)}
            size="medium"
            sx={{
              backgroundColor: ROLE_COLORS[nurse.role_type as keyof typeof ROLE_COLORS] || '#757575',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1rem',
              height: '36px',
              px: 1
            }}
          />
        </Box>
      </Box>
      <CardContent>
        <Grid container spacing={3}>
          {/* 基本情報 */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>基本情報</Typography>
            <Divider sx={{ mb: 2 }}/>
            <List dense>

              <ListItem>
                <InfoFieldItem label="配偶者" labelWidth="160px">
                  <Typography
                    component="span"
                    sx={getUnsetStyle(nurse.has_partner === null ? '[未入力]' : (nurse.has_partner ? 'あり' : 'なし'))}
                  >
                    {nurse.has_partner === null ? '[未入力]' : (nurse.has_partner ? 'あり' : 'なし')}
                  </Typography>
                </InfoFieldItem>
              </ListItem>

              <ListItem>
                <InfoFieldItem label="お子様" labelWidth="160px">
                  <Typography
                    component="span"
                    sx={getUnsetStyle(formatListValue(nurse.nurse_children_type_list, NURSE_CHILDREN_TYPES))}
                  >
                    {formatListValue(nurse.nurse_children_type_list, NURSE_CHILDREN_TYPES)}
                  </Typography>
                </InfoFieldItem>
              </ListItem>

              <ListItem>
                <InfoFieldItem label="ペット" labelWidth="160px">
                  <Typography
                    component="span"
                    sx={getUnsetStyle(formatListValue(nurse.nurse_pet_type_list, NURSE_PET_TYPES, nurse.nurse_pet_type_other))}
                  >
                    {formatListValue(nurse.nurse_pet_type_list, NURSE_PET_TYPES, nurse.nurse_pet_type_other)}
                  </Typography>
                </InfoFieldItem>
              </ListItem>

              <ListItem>
                <InfoFieldItem label="都道府県" labelWidth="160px">
                  <Typography
                    component="span"
                    sx={{ fontWeight: 'bold' }}
                  >
                    {getPrefectureLabel(nurse.prefecture_type)}
                  </Typography>
                </InfoFieldItem>
              </ListItem>

              <ListItem>
                <InfoFieldItem label="就労状況" labelWidth="160px">
                  <Typography
                    component="span"
                    sx={getUnsetStyle(getEmploymentLabel(nurse.employment_type) || '[未入力]')}
                  >
                    {getEmploymentLabel(nurse.employment_type) || '[未入力]'}
                  </Typography>
                </InfoFieldItem>
              </ListItem>

              <ListItem>
                <InfoFieldItem label="返信しやすい時間帯" labelWidth="160px">
                  <Typography
                    component="span"
                    sx={{ fontWeight: 'bold' }}
                  >
                    {getReplyPeriodLabel(nurse.reply_period_type)}
                  </Typography>
                </InfoFieldItem>
              </ListItem>

            </List>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>業務設定・経験情報</Typography>
            <Divider sx={{ mb: 2 }}/>
            <List dense>
              <ListItem>
                <InfoFieldItem label="看護経験" labelWidth="160px">
                  <Typography
                    component="span"
                    sx={{ fontWeight: 'bold' }}
                  >
                    {calculateNursingExperienceYears(nurse.nurse_license_pass_year)}
                  </Typography>
                </InfoFieldItem>
              </ListItem>
              <ListItem>
                <InfoFieldItem label="勤務経験のある施設" labelWidth="160px">
                  <Typography
                    component="span"
                    sx={getUnsetStyle(formatNursingExperience(nurse.nursing_work_experience_type_list, nurse.nursing_work_experience_type_other))}
                  >
                    {formatNursingExperience(nurse.nursing_work_experience_type_list, nurse.nursing_work_experience_type_other)}
                  </Typography>
                </InfoFieldItem>
              </ListItem>
              <ListItem>
                <InfoFieldItem label="がん看護経験" labelWidth="160px">
                  <Typography
                    component="span"
                    sx={getUnsetStyle(getCancerNursingExperienceLabel(nurse.cancer_nursing_experience_type))}
                  >
                    {getCancerNursingExperienceLabel(nurse.cancer_nursing_experience_type)}
                  </Typography>
                </InfoFieldItem>
              </ListItem>
              <ListItem>
                <InfoFieldItem label="がん罹患経験" labelWidth="160px">
                  <Typography
                    component="span"
                    sx={getUnsetStyle(nurse.cancer_experience === null ? '[未入力]' : (nurse.cancer_experience ? 'あり' : 'なし'))}
                  >
                    {nurse.cancer_experience === null ? '[未入力]' : (nurse.cancer_experience ? 'あり' : 'なし')}
                  </Typography>
                </InfoFieldItem>
              </ListItem>
              {nurse.cancer_patient_party_type_list && (
                <ListItem>
                  <InfoFieldItem label="身近のがん罹患者" labelWidth="160px">
                    <Typography
                      component="span"
                      sx={getUnsetStyle(formatCancerPatientPartyTypes(nurse.cancer_patient_party_type_list, nurse.cancer_patient_party_type_other))}
                    >
                      {formatCancerPatientPartyTypes(nurse.cancer_patient_party_type_list, nurse.cancer_patient_party_type_other)}
                    </Typography>
                  </InfoFieldItem>
                </ListItem>
              )}
            </List>
          </Grid>

          <Grid item xs={12}>
            <SectionWithPaper title="自己紹介" fullWidth>
              <Typography
                variant="body1"
                sx={
                  !nurse.introduction ?
                    { whiteSpace: 'pre-line', color: 'text.disabled', fontStyle: 'italic' } :
                    { whiteSpace: 'pre-line', fontWeight: 'bold' }
                }
              >
                {nurse.introduction || '[未入力]'}
              </Typography>
            </SectionWithPaper>
          </Grid>

          <Grid item xs={12}>
            <SectionWithPaper title="趣味・関心事" fullWidth>
              <Typography
                variant="body1"
                sx={
                  !nurse.matter_of_concern ?
                    { whiteSpace: 'pre-line', color: 'text.disabled', fontStyle: 'italic' } :
                    { whiteSpace: 'pre-line', fontWeight: 'bold' }
                }
              >
                {nurse.matter_of_concern || '[未入力]'}
              </Typography>
            </SectionWithPaper>
          </Grid>

          {/* 連絡先情報 */}
          <Grid item xs={12}>
            <SectionWithPaper title="連絡先情報" fullWidth>
              <InfoFieldItem label="メールアドレス" labelWidth="160px">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <MaskedEmailDisplay email={nurse.email}/>
                  <Button
                    onClick={handlePasswordResetClick}
                    size="small"
                    startIcon={<LockResetIcon fontSize="small"/>}
                    sx={{
                      color: 'primary.main',
                      textTransform: 'none',
                      ml: 2
                    }}
                  >
                    パスワードリセット
                  </Button>
                </Box>
              </InfoFieldItem>
              {nurse.reset_password_sent_at && passwordResetExpiry && (
                <Box sx={{ mt: 1, ml: '160px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TimeDisplay
                    timestamp={nurse.reset_password_sent_at}
                    prefix="パスワードリセット日時"
                    tooltipPrefix="パスワードリセット日時"
                    tooltipPlacement="top"
                    fontSize="0.85rem"
                    color="text.secondary"
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.85rem',
                      color: passwordResetExpiry.isExpired ? 'error.light' : 'text.secondary',
                      ml: 0.5
                    }}
                  >
                    ({passwordResetExpiry.isExpired ? passwordResetExpiry.remainingTime : `有効期限: ${passwordResetExpiry.remainingTime}`})
                  </Typography>
                </Box>
              )}
            </SectionWithPaper>
          </Grid>

          {/* メモ */}
          <Grid item xs={12}>
            <SectionWithPaper
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="h6" component="span">
                    メモ
                  </Typography>
                  <IconButton
                    onClick={handleEditClick}
                    size="small"
                    sx={{ color: 'primary.main' }}
                    title="メモを編集"
                  >
                    <EditIcon fontSize="small"/>
                  </IconButton>
                </Box>
              }
              fullWidth
            >
              <Typography
                variant="body1"
                sx={{
                  ...getUnsetStyle(nurse.senior_memo || '[未入力]'),
                  whiteSpace: 'pre-line',
                  fontWeight: nurse.senior_memo ? 'normal' : 'normal'
                }}
              >
                {nurse.senior_memo || '[未入力]'}
              </Typography>
            </SectionWithPaper>
          </Grid>

        </Grid>
      </CardContent>

      {/* メモ編集ダイアログ */}
      <MemoEditDialog
        open={isEditDialogOpen}
        memo={nurse.senior_memo || ''}
        onClose={() => setIsEditDialogOpen(false)}
        onSubmit={handleMemoSubmit}
      />

      {/* パスワードリセットダイアログ */}
      <PasswordResetDialog
        open={isPasswordResetDialogOpen}
        userId={nurse.id}
        userName={nurse.name}
        userType="nurse"
        onClose={() => {
          setIsPasswordResetDialogOpen(false);
          // ダイアログを閉じた後、看護師情報を更新してreset_password_sent_atを反映
          if (onNurseUpdate) {
            onNurseUpdate();
          }
        }}
      />

      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
        {nurse.current_sign_in_at ? (
          <TimeDisplay
            timestamp={nurse.current_sign_in_at}
            prefix="最終ログイン"
            tooltipPrefix="最終ログイン日時"
            tooltipPlacement="top"
          />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            最終ログイン: -
          </Typography>
        )}
        <TimeDisplay
          timestamp={nurse.updated_at}
          prefix="プロフィール更新"
          tooltipPrefix="プロフィール更新日時"
          tooltipPlacement="top"
        />
      </Box>
    </Card>
  );
};
export default ProfileSection;
